/**
 * Playlist generation: picking tracks under constraints, and saying honestly what happened.
 *
 * design/README.md §10 is unusually specific about the outcome, and it is the specification:
 *
 * > `41 of 50 tracks` … *"2h 14m · max 2 per artist is the binding constraint. Nine slots could
 * > not be filled without breaking it. Raising the cap to 3 would yield 50 of 50…"* with
 * > `Raise cap to 3` and `Keep 41` inline. **Never a silent short list.**
 *
 * Three obligations follow, and they are what this file is for.
 *
 * 1. **Name the binding constraint.** Not "some constraints applied" — *which* one, by name.
 * 2. **Count the cost.** How many slots it cost, so "nine" is a number and not a feeling.
 * 3. **Price the alternative.** What relaxing it by one step would actually yield — computed by
 *    running the pick again, never estimated, because an estimate that is wrong here is a button
 *    that lies about what it will do.
 */
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * A small deterministic PRNG (mulberry32).
 *
 * `Math.random()` would make a playlist unrepeatable, and §J15 lists `seed` among the recipe's
 * inputs precisely so that regenerating a saved recipe gives the same result. A shuffle nobody
 * can reproduce is not a recipe, it is a coin toss with a name.
 */
function rng(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
function ordered(candidates, constraints, now) {
    const pool = candidates.filter((c) => {
        if (constraints.minScore != null && c.score < constraints.minScore)
            return false;
        if (constraints.excludePlayedWithinDays != null && c.lastPlayedAt != null) {
            if (now - c.lastPlayedAt < constraints.excludePlayedWithinDays * DAY_MS)
                return false;
        }
        return true;
    });
    if (constraints.order === "shuffle") {
        const random = rng(constraints.seed ?? 1);
        // Fisher–Yates on a copy, driven by the seeded generator.
        const shuffled = [...pool];
        for (let i = shuffled.length - 1; i > 0; i -= 1) {
            const j = Math.floor(random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }
    // Score descending, then song id so ties do not reorder between two runs of one recipe.
    return [...pool].sort((a, b) => b.score - a.score || a.songId - b.songId);
}
/** One pass: walk the ordered pool taking what the per-artist and per-album caps allow. */
function pick(candidates, constraints, now) {
    const perArtist = new Map();
    const perAlbum = new Map();
    const picked = [];
    /** Slots a cap refused, so the report can name the one that cost most. */
    const refused = { maxPerArtist: 0, maxPerAlbum: 0, targetDuration: 0 };
    let durationMs = 0;
    let durationStopped = false;
    for (const candidate of ordered(candidates, constraints, now)) {
        if (picked.length >= constraints.limit)
            break;
        if (constraints.maxPerArtist != null && (perArtist.get(candidate.artistId) ?? 0) >= constraints.maxPerArtist) {
            refused.maxPerArtist += 1;
            continue;
        }
        if (constraints.maxPerAlbum != null && (perAlbum.get(candidate.albumId) ?? 0) >= constraints.maxPerAlbum) {
            refused.maxPerAlbum += 1;
            continue;
        }
        if (constraints.targetDurationMs != null && durationMs + (candidate.durationMs ?? 0) > constraints.targetDurationMs) {
            // Not a break: a shorter track further down the list can still fit, which is what a target
            // duration means. Breaking here would end the playlist on the first long song.
            refused.targetDuration += 1;
            durationStopped = true;
            continue;
        }
        picked.push(candidate);
        perArtist.set(candidate.artistId, (perArtist.get(candidate.artistId) ?? 0) + 1);
        perAlbum.set(candidate.albumId, (perAlbum.get(candidate.albumId) ?? 0) + 1);
        durationMs += candidate.durationMs ?? 0;
    }
    return { picked, durationMs, refused, durationStopped };
}
/**
 * Generates a playlist and reports on it.
 *
 * The counterfactual is RUN, not modelled: `relaxed.would` is the result of an actual second pick
 * with the constraint loosened by one. Modelling it would be cheaper and would eventually be
 * wrong — caps interact, and a button labelled "would yield 50 of 50" has to be right.
 */
export function generate(candidates, constraints, now = 0) {
    const run = pick(candidates, constraints, now);
    const shortBy = constraints.limit - run.picked.length;
    if (shortBy <= 0) {
        return {
            picked: run.picked,
            durationMs: run.durationMs,
            requested: constraints.limit,
            report: { binding: null, shortBy: 0, relaxed: null },
        };
    }
    /*
     * Which constraint is to blame. The caps that refused the most slots come first; if none did,
     * the pool itself was simply too small — and saying "not enough songs match" is a different and
     * more useful sentence than naming a cap that was never reached.
     */
    const blame = [
        ["maxPerArtist", run.refused.maxPerArtist ?? 0],
        ["maxPerAlbum", run.refused.maxPerAlbum ?? 0],
        ["targetDuration", run.durationStopped ? (run.refused.targetDuration ?? 0) : 0],
    ];
    const [name, cost] = blame.sort((a, b) => b[1] - a[1])[0];
    if (cost === 0) {
        // Nothing refused anything: the filters, not the caps, left too little to choose from.
        const poolCulprit = constraints.minScore != null && candidates.some((c) => c.score < constraints.minScore)
            ? "minScore"
            : constraints.excludePlayedWithinDays != null
                ? "excludePlayedWithin"
                : "pool";
        return {
            picked: run.picked,
            durationMs: run.durationMs,
            requested: constraints.limit,
            report: { binding: poolCulprit, shortBy, relaxed: relax(candidates, constraints, poolCulprit, now) },
        };
    }
    return {
        picked: run.picked,
        durationMs: run.durationMs,
        requested: constraints.limit,
        report: { binding: name, shortBy, relaxed: relax(candidates, constraints, name, now) },
    };
}
/** Runs the pick again with one constraint loosened by a single step. */
function relax(candidates, constraints, name, now) {
    const next = { ...constraints };
    let to;
    switch (name) {
        case "maxPerArtist":
            if (constraints.maxPerArtist == null)
                return null;
            to = constraints.maxPerArtist + 1;
            next.maxPerArtist = to;
            break;
        case "maxPerAlbum":
            if (constraints.maxPerAlbum == null)
                return null;
            to = constraints.maxPerAlbum + 1;
            next.maxPerAlbum = to;
            break;
        case "minScore":
            if (constraints.minScore == null || constraints.minScore <= 1)
                return null;
            to = constraints.minScore - 1;
            next.minScore = to;
            break;
        case "excludePlayedWithin":
            if (constraints.excludePlayedWithinDays == null)
                return null;
            // Halving rather than stepping: "30 days" and "29 days" are the same playlist, and a button
            // offering that would be a button that does nothing.
            to = Math.floor(constraints.excludePlayedWithinDays / 2);
            next.excludePlayedWithinDays = to === 0 ? null : to;
            break;
        default:
            // A target duration and an empty pool have no next step that is still the same request.
            return null;
    }
    return { name, to, would: pick(candidates, next, now).picked.length };
}
const CONSTRAINT_WORDS = {
    maxPerArtist: "max %d per artist",
    maxPerAlbum: "max %d per album",
    minScore: "minimum score %d",
    targetDuration: "the target length",
    excludePlayedWithin: "excluding anything played in the last %d days",
    pool: "the filter",
};
/**
 * The constraints half of a saved recipe, in the words the report already uses.
 *
 * A kept playlist is a decision, and the decision is the rules PLUS the constraints — a track list
 * with no recipe beside it cannot be read, argued with, or run again. Same vocabulary as
 * `reportSentence`, so "max 2 per artist" means one thing in the app.
 */
export function constraintSummary(constraints) {
    const parts = [`${constraints.limit} tracks`];
    if (constraints.maxPerArtist != null)
        parts.push(`max ${constraints.maxPerArtist} per artist`);
    if (constraints.maxPerAlbum != null)
        parts.push(`max ${constraints.maxPerAlbum} per album`);
    if (constraints.minScore != null)
        parts.push(`minimum score ${constraints.minScore}`);
    if (constraints.targetDurationMs != null)
        parts.push(`about ${durationLabel(constraints.targetDurationMs)}`);
    if (constraints.excludePlayedWithinDays != null) {
        parts.push(`nothing played in ${constraints.excludePlayedWithinDays} days`);
    }
    // Always last, and always said: a shuffle nobody can reproduce is not a recipe, so the seed
    // travels with it.
    parts.push(constraints.order === "shuffle" ? `shuffled, seed ${constraints.seed ?? 1}` : "by score");
    return parts;
}
/** `2h 14m`. */
export function durationLabel(ms) {
    const minutes = Math.round(ms / 60000);
    const hours = Math.floor(minutes / 60);
    return hours === 0 ? `${minutes}m` : `${hours}h ${minutes % 60}m`;
}
/**
 * The sentence the result panel shows, built from the report.
 *
 * Assembled here rather than in the component so that what the app claims about a playlist is
 * tested against the pick that produced it, not against a template.
 */
export function reportSentence(result, constraints) {
    const length = durationLabel(result.durationMs);
    if (result.report.binding === null)
        return `${length} · every slot filled.`;
    const value = result.report.binding === "maxPerArtist"
        ? constraints.maxPerArtist
        : result.report.binding === "maxPerAlbum"
            ? constraints.maxPerAlbum
            : result.report.binding === "minScore"
                ? constraints.minScore
                : result.report.binding === "excludePlayedWithin"
                    ? constraints.excludePlayedWithinDays
                    : null;
    const named = CONSTRAINT_WORDS[result.report.binding].replace("%d", String(value ?? ""));
    const slots = result.report.shortBy === 1 ? "One slot" : `${result.report.shortBy} slots`;
    const base = result.report.binding === "pool"
        ? `${length} · not enough songs match ${named}. ${slots} could not be filled.`
        : `${length} · ${named} is the binding constraint. ${slots} could not be filled without breaking it.`;
    if (result.report.relaxed === null)
        return base;
    const { to, would } = result.report.relaxed;
    const change = result.report.binding === "minScore"
        ? `Lowering it to ${to}`
        : result.report.binding === "excludePlayedWithin"
            ? `Shortening it to ${to} days`
            : `Raising the cap to ${to}`;
    return `${base} ${change} would yield ${would} of ${result.requested}.`;
}
//# sourceMappingURL=playlist.js.map